// src/components/DataTable.tsx
// Remove unused imports at the top:
import React, { useMemo, memo, useCallback } from 'react';
import type { ErrorInfo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import {
  Search,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  Briefcase,
  Shield,
  Square,
  CheckSquare
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import {
  renderContactColumn,
  renderPositionColumn,
  renderCompanyColumn,
  renderLocationColumn,
  renderEmailStatusColumn,
  renderSimpleCell,
  renderLinkedInCell
} from '../utils/cellRenderers';
import type { Contact } from '../types';
import './DataTable.css';


interface DataTableProps {
  loading?: boolean;
  currentListId?: string | null;
  initialLoadComplete?: boolean;
}

const SkeletonText: React.FC<{ width?: string; height?: string }> = ({
  width = '100%',
  height = '16px'
}) => (
  <div
    className="skeleton-text"
    style={{ width, height }}
  />
);

const generateSkeletonRows = (count: number): Contact[] => {
  return Array.from({ length: count }, (_, index) => ({
    UUID: `skeleton-${index}`,
    FIRST_NAME: 'Loading',
    LAST_NAME: 'Contact',
    JOB_TITLE: 'Loading Position',
    COMPANY_NAME: 'Loading Company',
    COMPANY_DOMAIN: 'loading.com',
    PERSONAL_CITY: 'Loading',
    PERSONAL_STATE: 'State',
    CURRENT_BUSINESS_EMAIL_VALIDATION_STATUS: 'valid',
    CURRENT_BUSINESS_EMAIL: 'loading@company.com',
    MOBILE_PHONES: '+1-555-0000',
    LAST_UPDATED: '2024-01-01'
  }));
};

const renderSkeletonContactCell = () => (
  <div className="contact-cell skeleton-cell">
    <div className="skeleton-avatar" />
    <div className="skeleton-contact-info">
      <SkeletonText width="120px" height="16px" />
      <SkeletonText width="90px" height="14px" />
    </div>
  </div>
);

const renderSkeletonCompanyCell = () => (
  <div className="company-cell skeleton-cell">
    <div className="skeleton-company-logo" />
    <div className="skeleton-company-info">
      <SkeletonText width="140px" height="16px" />
      <SkeletonText width="100px" height="14px" />
    </div>
  </div>
);

const renderSkeletonPositionCell = () => (
  <div className="position-cell skeleton-cell">
    <Briefcase size={12} className="skeleton-icon" />
    <SkeletonText width="110px" height="14px" />
  </div>
);

const renderSkeletonLocationCell = () => (
  <div className="location-cell skeleton-cell">
    <SkeletonText width="95px" height="14px" />
  </div>
);

const renderSkeletonEmailStatusCell = () => (
  <div className="status-indicator skeleton-cell">
    <Shield size={12} className="skeleton-icon" />
    <SkeletonText width="70px" height="14px" />
  </div>
);

const renderSkeletonTextCell = (width: string = '80px') => (
  <div className="skeleton-cell">
    <SkeletonText width={width} height="14px" />
  </div>
);

const renderSelectionCell = (
  isSelected: boolean,
  onClick: () => void,
  isSkeletonMode: boolean,
  selectAll: boolean,
  disabled: boolean
) => {
  if (isSkeletonMode) {
    return (
      <div className="selection-cell skeleton-cell">
        <Square size={18} className="selection-icon" />
      </div>
    );
  }

  return (
    <div className={`selection-cell${disabled ? ' disabled' : ''}`} onClick={disabled ? undefined : onClick} style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
      {(isSelected || selectAll) ? (
        <CheckSquare size={18} className="selection-icon selected" />
      ) : (
        <Square size={18} className="selection-icon" />
      )}
    </div>
  );
};

class DataTableErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('DataTable Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="table-container">
          <div className="table-error">
            <AlertTriangle size={48} />
            <h3>Data Display Error</h3>
            <p>Unable to display contact data. Please refresh the page.</p>
            <button
              className="button-primary"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const isValidContact = (contact: unknown): contact is Contact => {
  return contact !== null && typeof contact === 'object' && !Array.isArray(contact);
};

const safeFieldAccess = (contact: Contact, field: keyof Contact): string => {
  try {
    const value = contact[field];
    return value ? String(value) : '';
  } catch {
    return '';
  }
};

const DataTable: React.FC<DataTableProps> = memo(({ loading = false, initialLoadComplete = false }) => {
  const {
    state,
    setSorting,
    selectItem,
    deselectItem,
    selectAll
  } = useAppContext();

  const columnHelper = useMemo(() => createColumnHelper<Contact>(), []);

  const safeContacts = useMemo(() => {
    if (!Array.isArray(state.contacts)) {
      console.warn('Contacts is not an array:', state.contacts);
      return [];
    }

    return state.contacts.filter((contact, index) => {
      const isValid = isValidContact(contact);
      if (!isValid) {
        console.warn(`Invalid contact at index ${index}:`, contact);
      }
      return isValid;
    });
  }, [state.contacts]);

  const shouldShowSkeleton = useMemo(() => {
    return loading ||
      (state.contacts.length === 0 && !state.initialized) ||
      (state.contacts.length === 0 && state.loading);
  }, [loading, state.contacts.length, state.initialized, state.loading]);

  const displayData = useMemo(() => {
    if (shouldShowSkeleton) {
      return generateSkeletonRows(state.pageSize);
    } else {
      return safeContacts;
    }
  }, [shouldShowSkeleton, safeContacts, state.pageSize]);

  const isSkeletonMode = shouldShowSkeleton;

  const supportsSelection = useMemo(() => {
    if (!state.schema?.data_sources) return false;
    const currentDataSource = state.schema.data_sources[state.searchState.dataSource];
    return currentDataSource?.supports_selection || false;
  }, [state.schema, state.searchState.dataSource]);

  const isSchemaValid = useMemo(() => {
    return state.schema &&
      Array.isArray(state.schema.column_configs) &&
      Array.isArray(state.visibleColumns) &&
      state.visibleColumns.length > 0;
  }, [state.schema, state.visibleColumns]);

  const isSortableField = useCallback((sortField: string): boolean => {
    if (!state.schema?.field_definitions) return false;
    const fieldDef = state.schema.field_definitions[sortField];
    return Boolean(fieldDef?.sortable);
  }, [state.schema]);

  const visibleUUIDs = displayData.map(row => row.UUID).filter((uuid): uuid is string => Boolean(uuid));
  const selectedVisibleCount = visibleUUIDs.filter(uuid => state.selection.selectedIds.has(uuid)).length;
  const allVisibleSelected = visibleUUIDs.length > 0 && selectedVisibleCount === visibleUUIDs.length;

  // Disable select all when no filters are applied (for people data source)
  const hasActiveFilters = Object.keys(state.searchState.filters).length > 0;
  const selectAllDisabled = isSkeletonMode || (state.searchState.dataSource === 'people' && !hasActiveFilters);
  const selectionDisabled = isSkeletonMode;

  const handleSelectionClick = useCallback((uuid: string) => {
    if (!uuid || uuid.startsWith('skeleton-')) return;

    if (state.selection.selectAll && state.selection.selectedIds.size === 0) {
      selectAll(false);
      deselectItem(uuid as string);
      return;
    }

    const isSelected = state.selection.selectedIds.has(uuid);
    if (isSelected) {
      deselectItem(uuid as string);
    } else {
      selectItem(uuid as string);
    }
  }, [state.selection.selectedIds, state.selection.selectAll, selectItem, deselectItem, selectAll]);

  const handleSelectAllClick = useCallback(() => {
    // Prevent select all when no filters are applied for people data source
    if (state.searchState.dataSource === 'people' && !hasActiveFilters) {
      return;
    }
    selectAll(!state.selection.selectAll);
  }, [selectAll, state.selection.selectAll, state.searchState.dataSource, hasActiveFilters]);

  const columns: ColumnDef<Contact, unknown>[] = useMemo(() => {
    if (!isSchemaValid) {
      console.warn('Schema is invalid, returning empty columns');
      return [];
    }

    let tableColumns: ColumnDef<Contact, unknown>[] = [];

    if (supportsSelection) {
      tableColumns.push(
        columnHelper.display({
          id: 'selection',
          header: () => (
            <div
              className={`selection-header${selectAllDisabled ? ' disabled' : ''}`}
              onClick={selectAllDisabled ? undefined : handleSelectAllClick}
              style={selectAllDisabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
            >
              {state.selection.selectAll || allVisibleSelected ? (
                <CheckSquare size={18} className="selection-icon selected" />
              ) : (
                <Square size={18} className="selection-icon" />
              )}
            </div>
          ),
          cell: info => {
            const uuid = info.row.original.UUID;
            const isSelected = uuid ? state.selection.selectedIds.has(uuid as string) : false;
            return renderSelectionCell(
              isSelected,
              () => handleSelectionClick(uuid as string),
              isSkeletonMode,
              state.selection.selectAll,
              selectionDisabled
            );
          },
          size: 48,
          enableSorting: false
        })
      );
    }

    try {
      const visibleColumnConfigs = state.visibleColumns
        .map(columnId => state.schema!.column_configs.find(col => col.id === columnId))
        .filter((col): col is NonNullable<typeof col> => col !== undefined);

      tableColumns = [
        ...tableColumns,
        ...visibleColumnConfigs.map((colConfig) => {
          const sortField = colConfig.sort_field;
          const canSort = isSortableField(sortField);

          const headerComponent = () => (
            <div className="column-header-wrapper">
              <span className="column-label">{colConfig.label}</span>
            </div>
          );

          let cellRenderer: (props: { row: { original: Contact } }) => React.ReactNode;

          const safeCellRenderer = (originalRenderer: typeof cellRenderer, skeletonRenderer: () => React.ReactNode) => {
            return ({ row }: { row: { original: Contact } }) => {
              try {
                if (isSkeletonMode) {
                  return skeletonRenderer();
                }

                if (!isValidContact(row.original)) {
                  return <span>—</span>;
                }
                return originalRenderer({ row });
              } catch (error) {
                console.error(`Cell render error for ${colConfig.id}:`, error);
                return <span>Error</span>;
              }
            };
          };

          switch (colConfig.render_component) {
            case 'ContactCell':
              cellRenderer = safeCellRenderer(
                ({ row }) => renderContactColumn(row.original),
                renderSkeletonContactCell
              );
              break;
            case 'PositionCell':
              cellRenderer = safeCellRenderer(
                ({ row }) => renderPositionColumn(row.original),
                renderSkeletonPositionCell
              );
              break;
            case 'CompanyCell':
              cellRenderer = safeCellRenderer(
                ({ row }) => renderCompanyColumn(row.original),
                renderSkeletonCompanyCell
              );
              break;
            case 'LocationCell':
              cellRenderer = safeCellRenderer(
                ({ row }) => renderLocationColumn(row.original, 'personal'),
                renderSkeletonLocationCell
              );
              break;
            case 'EmailStatusCell':
              cellRenderer = safeCellRenderer(
                ({ row }) => renderEmailStatusColumn(row.original),
                renderSkeletonEmailStatusCell
              );
              break;
            case 'EmailCell':
              cellRenderer = safeCellRenderer(
                ({ row }) => (
                  <div className="email-cell">
                    <span>{safeFieldAccess(row.original, 'CURRENT_BUSINESS_EMAIL') || '—'}</span>
                  </div>
                ),
                () => renderSkeletonTextCell('140px')
              );
              break;
            case 'PhoneCell':
              cellRenderer = safeCellRenderer(
                ({ row }) => (
                  <div className="phone-cell">
                    <span>{safeFieldAccess(row.original, 'MOBILE_PHONES') || '—'}</span>
                  </div>
                ),
                () => renderSkeletonTextCell('120px')
              );
              break;
            case 'DateCell':
              cellRenderer = safeCellRenderer(
                ({ row }) => {
                  const value = safeFieldAccess(row.original, 'LAST_UPDATED');
                  if (!value) return <span>—</span>;
                  try {
                    const date = new Date(value);
                    return <span>{date.toLocaleDateString()}</span>;
                  } catch {
                    return <span>—</span>;
                  }
                },
                () => renderSkeletonTextCell('85px')
              );
              break;
            case 'SimpleCell':
              cellRenderer = safeCellRenderer(
                ({ row }) => renderSimpleCell(row.original, sortField),
                () => renderSkeletonTextCell('120px')
              );
              break;
            case 'LinkedInCell':
              cellRenderer = safeCellRenderer(
                ({ row }) => renderLinkedInCell(row.original),
                () => renderSkeletonTextCell('100px')
              );
              break;
            default:
              cellRenderer = safeCellRenderer(
                ({ row }) => {
                  const value = safeFieldAccess(row.original, sortField as keyof Contact);
                  return <span>{value || '—'}</span>;
                },
                () => renderSkeletonTextCell('90px')
              );
          }

          if (canSort) {
            return columnHelper.accessor(
              (row) => {
                const field = sortField as keyof Contact;
                return safeFieldAccess(row, field);
              },
              {
                id: colConfig.id,
                header: headerComponent,
                cell: cellRenderer,
                size: colConfig.width,
                enableSorting: true,
                sortingFn: 'alphanumeric',
                meta: { sortField }
              }
            );
          } else {
            return columnHelper.display({
              id: colConfig.id,
              header: headerComponent,
              cell: cellRenderer,
              size: colConfig.width,
              enableSorting: false,
              meta: { sortField }
            });
          }
        })
      ];

      return tableColumns;
    } catch (error) {
      console.error('Error building columns:', error);
      return [];
    }
  }, [
    columnHelper,
    state.schema,
    state.visibleColumns,
    isSortableField,
    isSchemaValid,
    isSkeletonMode,
    supportsSelection,
    state.selection.selectedIds,
    handleSelectAllClick,
    handleSelectionClick,
    state.selection.selectAll,
    selectionDisabled,
    selectAllDisabled,
    allVisibleSelected
  ]);

  const sortingState = useMemo(() => {
    if (!Array.isArray(state.searchState.sorting)) {
      console.warn('Sorting state is not an array:', state.searchState.sorting);
      return [];
    }

    return state.searchState.sorting.map(sort => ({
      id: sort.id,
      desc: sort.desc || false
    }));
  }, [state.searchState.sorting]);

  const handleSortingChange = useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    try {
      const newSorting = typeof updaterOrValue === 'function'
        ? updaterOrValue(sortingState)
        : updaterOrValue;

      console.log('🔄 Sorting changed:', newSorting);
      setSorting(newSorting);
    } catch (error) {
      console.error('Error updating sorting:', error);
    }
  }, [sortingState, setSorting]);

  const table = useReactTable<Contact>({
    data: displayData,
    columns,
    state: { sorting: sortingState },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    enableSortingRemoval: false,
    defaultColumn: {
      minSize: 24,
      maxSize: 500,
    }
  });

  if (!isSchemaValid) {
    return (
      <div className="table-container">
        <div className="no-columns-selected">
          <AlertTriangle size={48} />
          <h3>Configuration Error</h3>
          <p>Unable to load table configuration. Please refresh the page.</p>
          <button className="button-primary" onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  if (state.visibleColumns.length === 0) {
    return (
      <div className="table-container">
        <div className="no-columns-selected">
          <Search size={48} />
          <h3>No columns selected</h3>
          <p>Select columns from the sidebar to display data</p>
        </div>
      </div>
    );
  }

  const handleKeyDown = (header: { column: { getToggleSortingHandler: () => ((event: unknown) => void) | undefined } }) => {
    return (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const handler = header.column.getToggleSortingHandler();
        if (handler) {
          handler(e);
        }
      }
    };
  };

  return (
    <DataTableErrorBoundary>
      <div className="table-container">
        <div className="table-wrapper">
          <table className="data-table">
            <thead className="table-header">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    const canSort = header.column.getCanSort();
                    const sortDirection = header.column.getIsSorted();

                    return (
                      <th
                        key={header.id}
                        className={`table-header-cell ${canSort ? 'sortable' : ''} ${sortDirection ? `sorted-${sortDirection}` : ''}`}
                        style={{ width: header.getSize() }}
                      >
                        <div className="header-cell-content">
                          <div
                            className={`header-sortable-area ${canSort ? 'clickable' : ''}`}
                            onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                            onKeyDown={canSort ? handleKeyDown(header) : undefined}
                            role={canSort ? 'button' : undefined}
                            tabIndex={canSort ? 0 : undefined}
                            title={canSort ? 'Click to sort' : undefined}
                            style={{ cursor: canSort ? 'pointer' : 'default' }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && sortDirection && (
                              <span className="sort-indicator">
                                {sortDirection === 'desc' ? (
                                  <ChevronDown size={14} />
                                ) : (
                                  <ChevronUp size={14} />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="table-body">
              {!isSkeletonMode && displayData.length === 0 && state.initialized && !state.loading && initialLoadComplete ? (
                <tr>
                  <td colSpan={columns.length} style={{ padding: 0 }}>
                    <div className="no-results">
                      <Search size={48} />
                      <h3>No contacts found</h3>
                      <p>Try adjusting your search terms or filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => {
                  const rowId = row.original.UUID as string;
                  const isSelected = supportsSelection && rowId &&
                    (state.selection.selectAll || state.selection.selectedIds.has(rowId));

                  // ✅ SIMPLIFIED: Check if this row is being processed
                  const isProcessing = rowId && state.processing.uuids.has(rowId);

                  return (
                    <tr 
                      key={row.id} 
                      className={`data-row ${isSkeletonMode ? 'skeleton-row' : ''} ${isSelected ? 'selected-row' : ''} ${isProcessing ? 'processing-row' : ''}`}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} style={{ width: cell.column.getSize() }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DataTableErrorBoundary>
  );
});

DataTable.displayName = 'DataTable';

export default DataTable;